import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { MainLayout } from '@templates/index';
import { Card, Button } from '@atoms/index';
import { apiClient } from '@services/api';
import { useAuthStore } from '@store/authStore';
import { storage, STORAGE_KEYS } from '@utils/storage';

/**
 * Verify Email Page
 * Shows email verification sent message with resend functionality
 * Also handles email verification token from URL
 */
const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token')?.trim() ?? '';
  const emailFromUrl = searchParams.get('email')?.trim() ?? '';
  const emailFromState = (location.state as { email?: string })?.email;
  // Email priority: URL > State > ''
  const email = emailFromUrl || emailFromState || '';
  const [resendCooldown, setResendCooldown] = useState(60); // 60 seconds cooldown
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Countdown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-verify if token is in URL
  useEffect(() => {
    if (tokenFromUrl && !isVerifying && verificationStatus === 'idle') {
      handleVerifyEmail(tokenFromUrl);
    }
  }, [tokenFromUrl]);

  /**
   * Handle Email Verification
   */
  const handleVerifyEmail = async (token: string) => {
    const trimmedToken = typeof token === 'string' ? token.trim() : '';
    if (!trimmedToken) {
      setVerificationStatus('error');
      setIsVerifying(false);
      return;
    }
    setIsVerifying(true);
    setVerificationStatus('idle');

    try {
      const response = await apiClient.post('/auth/verify-email', { token: trimmedToken });
      const data = response.data.data;

      if (data.verified) {
        setVerificationStatus('success');
        
        // If tokens are provided (existing user auto-login), save them
        if (data.access_token && data.refresh_token) {
          const tokens = {
            access_token: data.access_token,
            refresh_token: data.refresh_token,
          };
          
          // Build user object if provided
          let userData = null;
          if (data.user) {
            userData = {
              id: data.user.id,
              email: data.user.email || '',
              phone: data.user.phone || '',
              name: data.user.name || '',
              surname: data.user.surname || '',
              role: data.user.role || 'USER',
              isVerified: true,
              createdAt: new Date().toISOString(),
            };
          }
          
          // Save tokens and user data to storage
          storage.set(STORAGE_KEYS.AUTH_TOKENS, tokens);
          if (userData) {
            storage.set(STORAGE_KEYS.USER_DATA, userData);
          }
          
          // Update auth store
          useAuthStore.setState({
            tokens,
            user: userData,
            isAuthenticated: true,
          });
        }
        
        // Redirect based on whether registration needs to be completed
        if (data.requires_complete_registration) {
          // Navigate to complete registration page with email
          setTimeout(() => navigate(`/complete-registration?email=${encodeURIComponent(data.email)}`), 2000);
        } else {
          // User already exists or registration complete, navigate to home
          setTimeout(() => navigate('/'), 2000);
        }
      }
    } catch (err: any) {
      console.error('Verification error:', err);
      setVerificationStatus('error');
    } finally {
      setIsVerifying(false);
    }
  };

  /**
   * Handle Resend Email
   */
  const handleResend = async () => {
    if (resendCooldown > 0 || !email) return;

    setIsResending(true);
    try {
      await apiClient.post('/auth/resend-verification-email', { email });
      setResendCooldown(60);
    } catch (err: any) {
      console.error('Resend error:', err);
    } finally {
      setIsResending(false);
    }
  };

  // Redirect if no email and no token
  if (!email && !tokenFromUrl) {
    navigate('/register');
    return null;
  }

  return (
    <MainLayout>
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-block p-4 rounded-3xl bg-gradient-to-br from-blue-500 to-cyan-600 shadow-premium mb-6">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              Doğrulama e-postası gönderildi
            </h1>
          </div>

          {/* Card */}
          <Card padding="none" className="overflow-hidden backdrop-blur-xl animate-scale-in">
            <div className="p-8">
              {/* Verification Status */}
              {verificationStatus === 'success' && (
                <div className="mb-6 p-4 rounded-xl bg-success/10 border border-success/30">
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 text-success flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-sm text-success font-medium">
                      E-posta adresiniz doğrulandı! Yönlendiriliyorsunuz...
                    </p>
                  </div>
                </div>
              )}

              {verificationStatus === 'error' && (
                <div className="mb-6 p-4 rounded-xl bg-error/10 border border-error/30">
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 text-error flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-sm text-error font-medium">
                      Doğrulama bağlantısı geçersiz veya süresi dolmuş.
                    </p>
                  </div>
                </div>
              )}

              {/* Email Message */}
              {verificationStatus === 'idle' && (
                <div className="text-center mb-6">
                  {isVerifying ? (
                    <>
                      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                      <p className="text-lg text-gray-700 mb-2">E-posta adresiniz doğrulanıyor...</p>
                    </>
                  ) : (
                    <>
                      <p className="text-lg text-gray-700 mb-2">
                        <span className="font-semibold">{email}</span> adresine bir doğrulama bağlantısı
                        yolladık.
                      </p>
                      <p className="text-sm text-gray-600">
                        Lütfen gelen kutunuzu ve diğer klasörlerini kontrol edin.
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Resend Button */}
              <div className="mt-8">
                <Button
                  type="button"
                  variant={resendCooldown > 0 ? 'outline' : 'primary'}
                  size="lg"
                  fullWidth
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || isResending}
                  isLoading={isResending}
                >
                  {resendCooldown > 0
                    ? `${resendCooldown} saniye içinde tekrar gönder`
                    : 'Tekrar Gönder'}
                </Button>
              </div>

              {/* Back to Register */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => navigate('/register')}
                  className="text-sm text-gray-600 hover:text-primary font-medium transition-colors"
                >
                  ← E-posta adresini değiştir
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default VerifyEmailPage;
